/**
 * The giant octopus (PRD §18). Not a big health bar: it lives inside the
 * terrain, reaches through the arena with independently-animated tentacles,
 * grabs the sub, throws debris, and retreats into darkness behind an ink cloud.
 *
 * Tentacles are simulated as angle chains and drawn as instanced segments, so
 * the whole creature is a few dozen instances of one mesh.
 */

import { damp, angleDelta, hyp } from '../core/math';
import { OCTOPUS } from './level';

/**
 * Where this octopus lives. Seeded from the mode's content by World, defaulting
 * to the campaign's trench so nothing changes for it. Survival puts one in a
 * lair under the arena instead, which is the whole reason this stopped being a
 * module-level import.
 */
type Lair = { x: number; y: number; lairX: number; lairY: number };

/**
 * How far the player must get, and for how long, before the animal gives up.
 *
 * 300 m is a little over three screen widths, so it cannot fire during a fight
 * that has simply drifted; eight seconds means backing off to reload never
 * ends the encounter.
 */
const LOSE_RANGE = 300;
const LOSE_TIME = 8;

export type OctoState = 'dormant' | 'stir' | 'emerge' | 'sweep' | 'grab' | 'hurt' | 'retreat' | 'dead';

const SEGMENTS = 13;
const TENTACLES = 6;

export interface Tentacle {
  /** World position of each joint, recomputed every frame. */
  px: Float32Array;
  py: Float32Array;
  seg: number;
  /** Length of each segment. */
  len: number;
  baseAngle: number;
  phase: number;
  /** 0 = coiled at the lair, 1 = fully extended at the target. */
  reach: number;
  targetReach: number;
  hp: number;
  maxHp: number;
  hitFlash: number;
  /** Where this tentacle is currently striking. */
  tx: number;
  ty: number;
  strike: number;
}

export class Octopus {
  /** Arena centre and lair, from content. */
  site: Lair = OCTOPUS;

  /** Move this octopus to a different lair. Called once, at world construction. */
  setSite(s: Lair) {
    this.site = s;
    this.x = s.lairX;
    this.y = s.lairY;
    this.reset();
  }

  x = OCTOPUS.lairX;
  y = OCTOPUS.lairY;
  vx = 0;
  vy = 0;
  hp = 900;
  maxHp = 900;
  state: OctoState = 'dormant';
  stateTime = 0;
  time = 0;
  hitFlash = 0;
  /** Eye glow, brightens when hunting. */
  arousal = 0;
  facing = -1;
  /** Set while the sub is held (PRD §18 — it grabs the submarine). */
  grabbed = false;
  grabTimer = 0;
  awake = false;
  /**
   * Seconds the player has spent outside `LOSE_RANGE`.
   *
   * Nothing ever un-woke this animal. `retreat` returns it to the lair and then
   * re-emerges, `reset` only runs when the player dies and `retire` is a
   * Survival-only call — so once roused it stayed roused across the entire
   * 7.9 km world. A player who swam away kept a full boss health bar pinned to
   * the HUD for a creature 700 m behind them, which is what was reported.
   */
  private farFor = 0;
  /** Set when it is going home to sleep rather than to re-emerge. */
  private disengaging = false;
  defeated = false;
  bodyRadius = 13;

  tentacles: Tentacle[] = [];

  constructor() {
    for (let i = 0; i < TENTACLES; i++) {
      const t = i / (TENTACLES - 1);
      this.tentacles.push({
        px: new Float32Array(SEGMENTS + 1),
        py: new Float32Array(SEGMENTS + 1),
        seg: SEGMENTS,
        len: 5.4 - Math.abs(t - 0.5) * 1.6,
        baseAngle: Math.PI * (0.62 + t * 0.76),
        phase: i * 1.37,
        reach: 0.12,
        targetReach: 0.12,
        hp: 90,
        maxHp: 90,
        hitFlash: 0,
        tx: this.x, ty: this.y,
        strike: 0,
      });
    }
  }

  get alive() {
    return this.state !== 'dead';
  }

  /**
   * Back to full strength and asleep in its lair. Called when the player dies:
   * a boss that keeps the damage you did before you were killed turns a fight
   * into an attrition puzzle, and this one is supposed to be a fight.
   */
  reset() {
    this.hp = this.maxHp;
    // Clears `defeated` too. The campaign only ever resets a live octopus (on
    // respawn), so it never mattered; Survival retires one at the start of a
    // run and revives it at wave 10, and without this it would come back
    // already dead and be skipped by the update guard.
    this.defeated = false;
    this.state = 'dormant';
    this.stateTime = 0;
    this.awake = false;
    this.grabbed = false;
    this.grabTimer = 0;
    this.hitFlash = 0;
    this.arousal = 0;
    this.x = this.site.lairX;
    this.y = this.site.lairY;
    this.vx = 0;
    this.vy = 0;
    for (const t of this.tentacles) {
      t.hp = t.maxHp;
      t.reach = 0.12;
      t.targetReach = 0.12;
      t.hitFlash = 0;
      t.strike = 0;
    }
  }

  /** Restore a save in which this thing was already beaten. */
  retire() {
    this.hp = 0;
    this.state = 'dead';
    this.stateTime = 99;
    this.defeated = true;
    this.awake = false;
    for (const t of this.tentacles) t.hp = 0;
  }

  /** Longest possible reach, used for arena framing and audio cues. */
  get span() {
    return SEGMENTS * 5.4;
  }

  private setState(s: OctoState) {
    this.state = s;
    this.stateTime = 0;
  }

  hurtBody(amount: number) {
    if (this.state === 'dead') return;
    this.hp -= amount;
    this.hitFlash = 1;
    this.arousal = 1;
    if (!this.awake) this.wake();
    if (this.hp <= 0) {
      this.hp = 0;
      this.setState('dead');
      this.defeated = true;
    } else if (this.state === 'sweep' && this.stateTime > 2.5) {
      // Stagger, but never often enough to stun-lock the fight.
      if (Math.random() < 0.22) this.setState('hurt');
    }
  }

  hurtTentacle(t: Tentacle, amount: number) {
    t.hp -= amount;
    t.hitFlash = 1;
    // Damage to a limb still hurts the animal, but far less than a body hit.
    this.hurtBody(amount * 0.35);
    if (t.hp <= 0) {
      t.hp = 0;
      t.targetReach = 0.05;
    }
  }

  wake() {
    if (this.awake) return;
    this.awake = true;
    this.farFor = 0;
    this.disengaging = false;
    this.setState('stir');
  }

  /** Nearest tentacle joint to a point, for projectile collision. */
  hitTest(x: number, y: number, r: number): { tentacle: Tentacle; joint: number } | null {
    for (const t of this.tentacles) {
      if (t.hp <= 0) continue;
      for (let i = 2; i <= t.seg; i++) {
        const rad = 2.6 * (1 - (i / t.seg) * 0.62) + 0.6;
        const dx = t.px[i] - x, dy = t.py[i] - y;
        if (dx * dx + dy * dy < (rad + r) ** 2) return { tentacle: t, joint: i };
      }
    }
    return null;
  }

  bodyHit(x: number, y: number, r: number): boolean {
    if (this.state === 'dormant' || this.state === 'retreat') return false;
    const dx = this.x - x, dy = this.y - y;
    return dx * dx + dy * dy < (this.bodyRadius + r) ** 2;
  }

  update(dt: number, ctx: {
    px: number; py: number;
    playerAlive: boolean;
    lightOn: boolean;
    sonarPinged: boolean;
    damagePlayer: (n: number) => void;
    pullPlayer: (x: number, y: number, strength: number) => void;
    onInk: (x: number, y: number) => void;
    onSediment: (x: number, y: number, n: number, power: number) => void;
    onShake: (n: number) => void;
    onRoar: (kind: 'wake' | 'strike' | 'hurt' | 'death') => void;
  }) {
    this.time += dt;
    this.stateTime += dt;
    this.hitFlash = damp(this.hitFlash, 0, 5, dt);

    const dx = ctx.px - this.x, dy = ctx.py - this.y;
    const dist = hyp(dx, dy);

    if (!this.awake) {
      // It notices light and sound long before it moves.
      if ((dist < 120 && (ctx.lightOn || ctx.sonarPinged)) || dist < 55) {
        this.wake();
        ctx.onRoar('wake');
      }
    }

    // LOSE INTEREST. It is an ambush predator sitting on a lair, not something
    // that follows you across an ocean — and a boss bar for a contact three
    // screens behind you is noise pretending to be information. Out of range
    // long enough and it goes home and sleeps; come back and it wakes again,
    // because `wake` is still driven by light, sonar and proximity.
    if (this.awake && this.state !== 'dead') {
      this.farFor = dist > LOSE_RANGE ? this.farFor + dt : 0;
      if (this.farFor > LOSE_TIME && !this.disengaging) {
        this.disengaging = true;
        this.setState('retreat');
      }
    }

    this.arousal = damp(this.arousal, this.state === 'dormant' ? 0.05 : dist < 150 ? 1 : 0.4, 1.6, dt);

    let homeX = this.site.lairX, homeY = this.site.lairY;
    let reachTarget = 0.1;

    switch (this.state) {
      case 'dormant':
        break;

      case 'stir':
        // Tentacles uncoil first — the body stays hidden.
        reachTarget = 0.34 + Math.sin(this.time * 0.8) * 0.1;
        ctx.onSediment(this.x, this.y, 1, 0.6);
        if (this.stateTime > 2.6) this.setState('emerge');
        break;

      case 'emerge':
        homeX = this.site.x;
        homeY = this.site.y;
        reachTarget = 0.55;
        if (this.stateTime > 2.2) {
          this.setState('sweep');
          ctx.onRoar('strike');
        }
        break;

      case 'sweep': {
        homeX = this.site.x + Math.sin(this.time * 0.31) * 46;
        homeY = this.site.y + Math.sin(this.time * 0.24 + 1.4) * 16;
        reachTarget = 0.95;
        // Each tentacle strikes on its own rhythm.
        for (let i = 0; i < this.tentacles.length; i++) {
          const t = this.tentacles[i];
          if (t.hp <= 0) continue;
          const beat = (this.time * 0.55 + i * 0.9) % 4.4;
          if (beat < 1.1 && dist < this.span * 0.95) {
            t.strike = damp(t.strike, 1, 5, dt);
            t.tx = damp(t.tx, ctx.px, 3.4, dt);
            t.ty = damp(t.ty, ctx.py, 3.4, dt);
          } else {
            t.strike = damp(t.strike, 0, 2.4, dt);
            const a = t.baseAngle + Math.sin(this.time * 0.7 + t.phase) * 0.5;
            t.tx = damp(t.tx, this.x + Math.cos(a) * 44, 1.6, dt);
            t.ty = damp(t.ty, this.y + Math.sin(a) * 34 + 12, 1.6, dt);
          }
        }
        if (this.stateTime > 9 && dist < 26 && ctx.playerAlive) {
          this.setState('grab');
          ctx.onRoar('strike');
        }
        if (this.stateTime > 15) this.setState('retreat');
        break;
      }

      case 'grab':
        homeX = this.site.x;
        homeY = this.site.y;
        reachTarget = 1;
        this.grabbed = dist < 30;
        if (this.grabbed) {
          // Drag the sub toward the beak and grind on it.
          ctx.pullPlayer(this.x + this.facing * 10, this.y, 26);
          ctx.damagePlayer(16 * dt);
          ctx.onShake(0.5 * dt * 8);
        }
        if (this.stateTime > 3.4) {
          this.grabbed = false;
          this.setState('sweep');
        }
        break;

      case 'hurt':
        reachTarget = 0.5;
        homeX = this.site.x - 22;
        homeY = this.site.y - 6;
        if (this.stateTime === 0) ctx.onRoar('hurt');
        if (this.stateTime > 1.5) this.setState(this.hp < this.maxHp * 0.4 ? 'retreat' : 'sweep');
        break;

      case 'retreat':
        reachTarget = 0.18;
        homeX = this.site.lairX;
        homeY = this.site.lairY;
        if (this.stateTime < dt * 2) {
          ctx.onInk(this.x, this.y);
          ctx.onSediment(this.x, this.y, 24, 1.6);
        }
        if (this.stateTime > 4.5) {
          if (this.disengaging) {
            // All the way down, and asleep. `dormant` is what `wake` tests, so
            // this is a real reset of the encounter rather than a pause in it.
            this.awake = false;
            this.disengaging = false;
            this.farFor = 0;
            this.setState('dormant');
          } else {
            this.setState('emerge');
          }
        }
        break;

      case 'dead':
        reachTarget = 0.08;
        homeY = this.site.lairY - 6;
        homeX = this.site.lairX;
        if (this.stateTime < dt * 2) {
          ctx.onInk(this.x, this.y);
          ctx.onRoar('death');
        }
        break;
    }

    // Body drifts toward its goal — heavy, never snappy.
    const ax = (homeX - this.x) * 1.35;
    const ay = (homeY - this.y) * 1.35;
    this.vx = damp(this.vx, ax, 2.2, dt);
    this.vy = damp(this.vy, ay, 2.2, dt);
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    if (Math.abs(dx) > 4) this.facing = Math.sign(dx);

    // Something this big shifts the water it moves through.
    if (hyp(this.vx, this.vy) > 6 && Math.random() < dt * 12) {
      ctx.onSediment(this.x, this.y + 6, 1, 1.1);
    }

    this.solveTentacles(dt, reachTarget, ctx);
  }

  private solveTentacles(dt: number, reachTarget: number, ctx: Parameters<Octopus['update']>[1]) {
    for (let ti = 0; ti < this.tentacles.length; ti++) {
      const t = this.tentacles[ti];
      t.hitFlash = damp(t.hitFlash, 0, 5, dt);
      if (t.hp > 0) t.targetReach = reachTarget;
      t.reach = damp(t.reach, t.targetReach, 2.1, dt);

      // Root at the mantle, fanned out around the underside.
      const rootA = t.baseAngle;
      let x = this.x + Math.cos(rootA) * 6.5 * this.facing;
      let y = this.y + Math.sin(rootA) * 4.5 - 3;
      t.px[0] = x;
      t.py[0] = y;

      // Aim the chain at the strike target, curling more toward the tip.
      const aimA = Math.atan2(t.ty - y, t.tx - x);
      let ang = rootA * (1 - t.strike * 0.8) + aimA * (t.strike * 0.8);

      const wave = Math.sin(this.time * 1.7 + t.phase) * 0.16 + Math.sin(this.time * 0.9 + t.phase * 2.1) * 0.1;
      const curl = (1 - t.reach) * 0.62;

      for (let i = 1; i <= t.seg; i++) {
        const k = i / t.seg;
        const segLen = t.len * (0.55 + t.reach * 0.75) * (1 - k * 0.28);
        // Progressive bend gives the limb its boneless S-curve.
        const bend = curl * (0.5 + k) + wave * (0.35 + k * 1.5) * (0.3 + t.reach);
        const targetA = aimA;
        ang += angleDelta(ang, targetA) * (0.16 * t.strike * (0.4 + k)) + bend * 0.34;
        x += Math.cos(ang) * segLen;
        y += Math.sin(ang) * segLen;
        t.px[i] = x;
        t.py[i] = y;
      }

      // Tip contact hurts (PRD §18 — tentacles are the threat, not the body).
      if (t.hp > 0 && t.strike > 0.35 && ctx.playerAlive) {
        const tipX = t.px[t.seg], tipY = t.py[t.seg];
        const d = hyp(tipX - ctx.px, tipY - ctx.py);
        if (d < 5.5) {
          ctx.damagePlayer(26 * dt * t.strike);
          ctx.onShake(dt * 2.6);
          ctx.onSediment(tipX, tipY, 1, 1);
        }
      }
    }
  }
}
